import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import {
  Route, MapPin, CalendarDays, Users, Briefcase, Car, Sparkles, User, CreditCard,
  Loader2, Check, Clock, ArrowRightLeft, ArrowRight, Repeat, Timer,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useConfig } from "@/hooks/useConfig";
import { useCustomerAuth } from "@/lib/customerAuth";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { api, euro, formatApiError } from "@/lib/api";

const TRIP_TYPES = [
  { id: "simple", label: "Trajet Simple", icon: ArrowRight },
  { id: "round", label: "Aller-Retour", icon: Repeat },
  { id: "hourly", label: "Forfait Heure", icon: Timer },
];

const Section = ({ n, icon: Icon, title, subtitle, children }) => (
  <div className="border-b border-white/10 py-10 lg:py-14">
    <div className="flex items-center gap-4 mb-8">
      <span className="grid h-11 w-11 place-items-center rounded-full border border-gold/30 text-gold shrink-0">
        <Icon size={18} />
      </span>
      <div>
        <p className="text-[11px] tracking-[0.3em] uppercase text-white/40">Étape {n}</p>
        <h2 className="font-serif text-2xl text-white">{title}</h2>
        {subtitle && <p className="text-sm text-white/45 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

export default function Booking() {
  const { config } = useConfig();
  const { customer: account } = useCustomerAuth();
  const vehicles = config?.vehicles || [];
  const options = config?.options || [];
  const savedAddresses = account?.saved_addresses || [];
  const [params] = useSearchParams();

  const [tripType, setTripType] = useState("simple");
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [hours, setHours] = useState("2");
  const [passengers, setPassengers] = useState("1");
  const [luggage, setLuggage] = useState("1");
  const [vehicleId, setVehicleId] = useState("berline");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [customer, setCustomer] = useState({
    civility: "M.", first_name: "", last_name: "", email: "", phone: "", instructions: "",
  });

  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  // Prefill from query params (services / fleet CTAs)
  useEffect(() => {
    const v = params.get("vehicle");
    const t = params.get("trip");
    if (v) setVehicleId(v);
    if (t && ["simple", "round", "hourly"].includes(t)) setTripType(t);
  }, [params]);

  // Prefill customer details when logged in
  useEffect(() => {
    if (account) {
      setCustomer((c) => ({
        ...c,
        first_name: c.first_name || account.first_name || "",
        last_name: c.last_name || account.last_name || "",
        email: c.email || account.email || "",
        phone: c.phone || account.phone || "",
      }));
    }
  }, [account]);

  const setCust = (k) => (e) => setCustomer((c) => ({ ...c, [k]: e.target.value }));
  const toggleOption = (id) =>
    setSelectedOptions((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectedVehicle = useMemo(() => vehicles.find((v) => v.id === vehicleId), [vehicles, vehicleId]);

  // Live estimate
  useEffect(() => {
    if (!vehicleId) return;
    const ready = tripType === "hourly" ? !!hours : pickup.trim() && dropoff.trim();
    if (!ready) {
      setEstimate(null);
      return;
    }
    setEstimating(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/booking/estimate", {
          trip_type: tripType,
          vehicle_id: vehicleId,
          pickup,
          dropoff,
          hours: parseInt(hours, 10),
          options: selectedOptions,
          pickup_lat: pickupCoords?.lat ?? null,
          pickup_lng: pickupCoords?.lng ?? null,
          dropoff_lat: dropoffCoords?.lat ?? null,
          dropoff_lng: dropoffCoords?.lng ?? null,
        });
        setEstimate(data);
      } catch (e) {
        setEstimate(null);
      } finally {
        setEstimating(false);
      }
    }, 650);
    return () => clearTimeout(debounceRef.current);
  }, [tripType, vehicleId, pickup, dropoff, hours, selectedOptions, pickupCoords, dropoffCoords]);

  const validate = () => {
    if (tripType === "hourly") {
      if (!pickup.trim()) return "Indiquez le lieu de prise en charge.";
    } else if (!pickup.trim() || !dropoff.trim()) {
      return "Indiquez les lieux de départ et d'arrivée.";
    }
    if (!date) return "Sélectionnez une date.";
    if (!time) return "Sélectionnez une heure.";
    if (!customer.first_name || !customer.last_name) return "Indiquez votre nom et prénom.";
    if (!customer.email) return "Indiquez votre email.";
    if (!customer.phone) return "Indiquez votre téléphone.";
    if (!estimate) return "Le tarif est en cours de calcul, patientez un instant.";
    return null;
  };

  const pay = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/booking/checkout", {
        trip_type: tripType,
        vehicle_id: vehicleId,
        pickup,
        dropoff,
        date: date ? format(date, "yyyy-MM-dd") : "",
        time,
        hours: parseInt(hours, 10),
        passengers: parseInt(passengers, 10),
        luggage: parseInt(luggage, 10),
        options: selectedOptions,
        pickup_lat: pickupCoords?.lat ?? null,
        pickup_lng: pickupCoords?.lng ?? null,
        dropoff_lat: dropoffCoords?.lat ?? null,
        dropoff_lng: dropoffCoords?.lng ?? null,
        customer,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossible de démarrer le paiement.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page" className="pt-28">
      <section className="mx-auto max-w-7xl px-5 lg:px-10 pt-6 pb-4">
        <p className="text-gold text-xs tracking-[0.4em] uppercase">Réservation en ligne</p>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-white">Réservez votre course</h1>
        <p className="mt-4 max-w-2xl text-white/60">
          Un tunnel fluide et sécurisé : configurez votre trajet, choisissez votre véhicule, payez en ligne.
        </p>
      </section>

      <div className="mx-auto max-w-7xl px-5 lg:px-10 grid lg:grid-cols-[1fr_380px] gap-12 pb-24">
        {/* FORM COLUMN */}
        <div>
          {/* Step 1 - Trip type */}
          <Section n="1" icon={Route} title="Type de trajet">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="trip-type-group">
              {TRIP_TYPES.map((t) => {
                const active = tripType === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`trip-${t.id}`}
                    onClick={() => setTripType(t.id)}
                    className={`flex items-center gap-3 rounded-xl border px-5 py-4 text-left transition-colors duration-200 ${
                      active ? "border-gold bg-gold/10 text-white" : "border-white/15 text-white/70 hover:border-gold/40"
                    }`}
                  >
                    <t.icon size={18} className={active ? "text-gold" : "text-white/50"} />
                    <span className="text-sm">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Step 2 - Itinerary */}
          <Section n="2" icon={MapPin} title="Itinéraire" subtitle="Adresses de prise en charge et destination">
            <div className="space-y-5">
              <div>
                <Label className="text-white/70">Lieu de prise en charge *</Label>
                <div className="mt-2">
                  <AddressAutocomplete
                    testid="pickup-input" listTestid="pickup-suggestions"
                    value={pickup}
                    onChange={(v) => { setPickup(v); setPickupCoords(null); }}
                    onSelect={(a) => { setPickup(a.label); setPickupCoords({ lat: a.lat, lng: a.lng }); }}
                    placeholder="Adresse, aéroport, gare…"
                  />
                </div>
                {savedAddresses.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {savedAddresses.map((a) => (
                      <button key={a.id} type="button" data-testid={`pickup-saved-${a.id}`}
                        onClick={() => { setPickup(a.address); setPickupCoords(a.lat ? { lat: a.lat, lng: a.lng } : null); }}
                        className="rounded-full border border-white/15 px-3 py-1 text-xs text-white/60 hover:border-gold/50 hover:text-gold transition-colors">
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {tripType !== "hourly" && (
                <div>
                  <Label className="text-white/70">Destination *</Label>
                  <div className="mt-2">
                    <AddressAutocomplete
                      testid="dropoff-input" listTestid="dropoff-suggestions"
                      value={dropoff}
                      onChange={(v) => { setDropoff(v); setDropoffCoords(null); }}
                      onSelect={(a) => { setDropoff(a.label); setDropoffCoords({ lat: a.lat, lng: a.lng }); }}
                      placeholder="Adresse de destination…"
                    />
                  </div>
                  {savedAddresses.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {savedAddresses.map((a) => (
                        <button key={a.id} type="button" data-testid={`dropoff-saved-${a.id}`}
                          onClick={() => { setDropoff(a.address); setDropoffCoords(a.lat ? { lat: a.lat, lng: a.lng } : null); }}
                          className="rounded-full border border-white/15 px-3 py-1 text-xs text-white/60 hover:border-gold/50 hover:text-gold transition-colors">
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {tripType === "hourly" && (
                <div>
                  <Label className="text-white/70">Durée de mise à disposition</Label>
                  <Select value={hours} onValueChange={setHours}>
                    <SelectTrigger data-testid="hours-select" className="mt-2 bg-ink border-white/15 focus:ring-gold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2, 3, 4, 5, 6, 8, 10, 12].map((h) => (
                        <SelectItem key={h} value={String(h)}>{h} heures</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <datalist id="places">
              </datalist>
            </div>
          </Section>

          {/* Step 3 - Date & time */}
          <Section n="3" icon={CalendarDays} title="Date & Heure">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-white/70">Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      data-testid="date-trigger"
                      className="mt-2 w-full flex items-center gap-3 rounded-md border border-white/15 bg-ink px-3 py-2.5 text-sm text-left text-white/90 hover:border-gold/40 transition-colors"
                    >
                      <CalendarDays size={16} className="text-gold" />
                      {date ? format(date, "PPP", { locale: fr }) : <span className="text-white/40">Choisir une date</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-panel border-white/15" align="start">
                    <Calendar
                      mode="single" selected={date} onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-white/70">Heure *</Label>
                <Input
                  data-testid="time-input" type="time" value={time} onChange={(e) => setTime(e.target.value)}
                  className="mt-2 bg-ink border-white/15 focus-visible:ring-gold"
                />
              </div>
            </div>
          </Section>

          {/* Step 4 - Passengers */}
          <Section n="4" icon={Users} title="Passagers & Bagages">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-white/70">Nombre de passagers</Label>
                <Select value={passengers} onValueChange={setPassengers}>
                  <SelectTrigger data-testid="passengers-select" className="mt-2 bg-ink border-white/15 focus:ring-gold"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <SelectItem key={n} value={String(n)}>{n} passager{n > 1 ? "s" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/70">Nombre de bagages</Label>
                <Select value={luggage} onValueChange={setLuggage}>
                  <SelectTrigger data-testid="luggage-select" className="mt-2 bg-ink border-white/15 focus:ring-gold"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => <SelectItem key={n} value={String(n)}>{n} bagage{n > 1 ? "s" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Section>

          {/* Step 5 - Vehicle */}
          <Section n="5" icon={Car} title="Sélection du véhicule" subtitle="Le tarif s'ajuste selon votre choix">
            <div className="grid sm:grid-cols-3 gap-4" data-testid="vehicle-group">
              {vehicles.map((v) => {
                const active = vehicleId === v.id;
                return (
                  <button
                    key={v.id}
                    data-testid={`vehicle-${v.id}`}
                    onClick={() => setVehicleId(v.id)}
                    className={`text-left rounded-xl border overflow-hidden transition-all duration-200 ${
                      active ? "border-gold ring-1 ring-gold" : "border-white/15 hover:border-gold/40"
                    }`}
                  >
                    <div className="relative">
                      <img src={v.image} alt={v.name} className="h-28 w-full object-cover" />
                      {active && (
                        <span className="absolute top-2 right-2 grid h-6 w-6 place-items-center rounded-full bg-gold text-black">
                          <Check size={14} />
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-white text-sm">{v.name}</p>
                      <p className="text-white/45 text-xs mt-0.5">{v.seats} pers · {v.luggage} bag.</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Step 6 - Options */}
          <Section n="6" icon={Sparkles} title="Options supplémentaires">
            <div className="space-y-3" data-testid="options-group">
              {options.map((o) => {
                const checked = selectedOptions.includes(o.id);
                return (
                  <label
                    key={o.id}
                    data-testid={`option-${o.id}`}
                    className={`flex items-center justify-between gap-4 rounded-xl border px-5 py-4 cursor-pointer transition-colors duration-200 ${
                      checked ? "border-gold/60 bg-gold/5" : "border-white/12 hover:border-white/25"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <Checkbox checked={checked} onCheckedChange={() => toggleOption(o.id)} className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold data-[state=checked]:text-black" />
                      <span className="text-sm text-white/80">{o.label}</span>
                    </span>
                    <span className="text-sm text-gold">{o.price > 0 ? `+ ${euro(o.price)}` : "Offert"}</span>
                  </label>
                );
              })}
            </div>
          </Section>

          {/* Step 7 - Customer */}
          <Section n="7" icon={User} title="Vos coordonnées">
            <div className="space-y-5">
              <div className="grid sm:grid-cols-[120px_1fr_1fr] gap-5">
                <div>
                  <Label className="text-white/70">Civilité</Label>
                  <Select value={customer.civility} onValueChange={(v) => setCustomer((c) => ({ ...c, civility: v }))}>
                    <SelectTrigger data-testid="civility-select" className="mt-2 bg-ink border-white/15 focus:ring-gold"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M.">M.</SelectItem>
                      <SelectItem value="Mme">Mme</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/70">Prénom *</Label>
                  <Input data-testid="firstname-input" value={customer.first_name} onChange={setCust("first_name")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
                </div>
                <div>
                  <Label className="text-white/70">Nom *</Label>
                  <Input data-testid="lastname-input" value={customer.last_name} onChange={setCust("last_name")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-white/70">Email *</Label>
                  <Input data-testid="email-input" type="email" value={customer.email} onChange={setCust("email")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
                </div>
                <div>
                  <Label className="text-white/70">Téléphone *</Label>
                  <Input data-testid="phone-input" value={customer.phone} onChange={setCust("phone")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" placeholder="06 12 34 56 78" />
                </div>
              </div>
              <div>
                <Label className="text-white/70">Instructions particulières</Label>
                <Textarea data-testid="instructions-input" value={customer.instructions} onChange={setCust("instructions")} rows={3} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" placeholder="Code d'accès, étage, numéro de vol…" />
              </div>
            </div>
          </Section>
        </div>

        {/* SUMMARY COLUMN */}
        <div className="lg:sticky lg:top-28 h-fit">
          <div data-testid="booking-summary" className="rounded-2xl border border-gold/25 bg-panel overflow-hidden">
            <div className="px-7 py-5 border-b border-white/10 flex items-center gap-2">
              <CreditCard size={16} className="text-gold" />
              <h3 className="text-white font-serif text-lg">Récapitulatif</h3>
            </div>
            <div className="px-7 py-6 space-y-4 text-sm">
              <SumRow icon={ArrowRightLeft} label="Trajet" value={TRIP_TYPES.find((t) => t.id === tripType)?.label} />
              <SumRow icon={MapPin} label="Départ" value={pickup || "—"} />
              {tripType !== "hourly"
                ? <SumRow icon={MapPin} label="Arrivée" value={dropoff || "—"} />
                : <SumRow icon={Clock} label="Durée" value={`${hours} heures`} />}
              <SumRow icon={CalendarDays} label="Quand" value={date ? `${format(date, "dd/MM/yyyy")} ${time}` : "—"} />
              <SumRow icon={Car} label="Véhicule" value={selectedVehicle?.name || "—"} />
              {estimate && !estimate.approximate && tripType !== "hourly" && (
                <SumRow icon={Route} label="Distance est." value={`${estimate.distance_km} km`} />
              )}
              {selectedOptions.length > 0 && (
                <div className="flex items-start gap-3">
                  <Sparkles size={15} className="text-white/40 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-white/40 text-xs">Options</p>
                    {options.filter((o) => selectedOptions.includes(o.id)).map((o) => (
                      <p key={o.id} className="text-white/80">{o.label}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-7 py-5 border-t border-white/10 bg-ink/40">
              {estimate && (
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between text-white/55"><span>Course</span><span>{euro(estimate.base_price)}</span></div>
                  {estimate.options_price > 0 && (
                    <div className="flex justify-between text-white/55"><span>Options</span><span>{euro(estimate.options_price)}</span></div>
                  )}
                </div>
              )}
              <div className="flex items-end justify-between">
                <span className="text-white/60 text-sm">Total TTC</span>
                <span data-testid="total-price" className="gold-text font-serif text-3xl">
                  {estimating ? "…" : estimate ? euro(estimate.total_price) : "—"}
                </span>
              </div>
              {estimate?.approximate && (
                <p className="mt-2 text-[11px] text-white/40">Estimation approximative — le tarif exact sera confirmé.</p>
              )}

              <button
                data-testid="pay-button"
                onClick={pay}
                disabled={submitting}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-goldsoft disabled:opacity-60"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Redirection…</> : <><CreditCard size={16} /> Payer et Réserver</>}
              </button>
              <p className="mt-3 flex items-center justify-center gap-2 text-[11px] text-white/40">
                <Check size={12} className="text-gold" /> Paiement sécurisé par Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const SumRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon size={15} className="text-white/40 mt-0.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-white/40 text-xs">{label}</p>
      <p className="text-white/85 break-words">{value}</p>
    </div>
  </div>
);
